export type LegacyCreatelyRegion = 'us' | 'eu' | 'au';

export interface LegacyCustomImageUrlOptions {
  region?: string | null;
  /** Full template with `{id}` or a prefix ending in `/` */
  legacyCustomImageBaseUrl?: string;
}

/** Production Neutrino custom-image buckets (per region). */
const PROD_LEGACY_CUSTOM_IMAGE_BASE: Record<LegacyCreatelyRegion, string> = {
  us: 'https://s3.amazonaws.com/neutrino-custom-image/',
  eu: 'https://neutrino-custom-image-eu.s3.eu-west-1.amazonaws.com/',
  au: 'https://neutrino-custom-image-au.s3.ap-southeast-2.amazonaws.com/',
};

/**
 * Stage2 uses a single shared bucket for all regions (matches app-stage2 / wrangler pre-prod).
 * Bucket name is `neutrino-custom-image-stage2` (not `stage`).
 */
const LOCAL_DEV_LEGACY_CUSTOM_IMAGE_BASE: Record<LegacyCreatelyRegion, string> = {
  us: 'https://s3.amazonaws.com/neutrino-custom-image-stage2/',
  eu: 'https://s3.amazonaws.com/neutrino-custom-image-stage2/',
  au: 'https://s3.amazonaws.com/neutrino-custom-image-stage2/',
};

function readViteEnv(key: string): string | undefined {
  if (typeof import.meta === 'undefined') return undefined;
  const env = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env;
  const value = env?.[key];
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined;
}

function isLocalDevelopment(): boolean {
  if (typeof import.meta === 'undefined') return false;
  const env = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env;
  if (env?.DEV === true) return true;
  if (env?.MODE === 'development') return true;
  const siteUrl = readViteEnv('VITE_SITE_URL') ?? '';
  if (/stage2|localhost/i.test(siteUrl)) return true;
  const legacyAppUrl = readViteEnv('VITE_LEGACY_APP_URL') ?? '';
  if (/stage2|localhost/i.test(legacyAppUrl)) return true;
  const baseUrl = readViteEnv('VITE_BASE_URL') ?? '';
  if (/localhost|127\.0\.0\.1/i.test(baseUrl)) return true;
  return false;
}

function defaultLegacyCustomImageBase(region: LegacyCreatelyRegion): string {
  return isLocalDevelopment()
    ? LOCAL_DEV_LEGACY_CUSTOM_IMAGE_BASE[region]
    : PROD_LEGACY_CUSTOM_IMAGE_BASE[region];
}

function normalizeRegion(region?: string | null): LegacyCreatelyRegion {
  const normalized = (region || 'us').trim().toLowerCase();
  if (normalized === 'eu') return 'eu';
  if (normalized === 'au') return 'au';
  return 'us';
}

export function isAbsoluteMediaUrl(value: string): boolean {
  return /^(https?:\/\/|data:|blob:|\/)/i.test(value.trim());
}

export function resolveLegacyCustomImageBaseUrl(
  options?: LegacyCustomImageUrlOptions,
): string {
  const override = options?.legacyCustomImageBaseUrl?.trim();
  if (override) return override;

  const region = normalizeRegion(options?.region);
  switch (region) {
    case 'eu':
      return readViteEnv('VITE_LEGACY_CUSTOM_IMAGE_EU') ?? defaultLegacyCustomImageBase('eu');
    case 'au':
      return readViteEnv('VITE_LEGACY_CUSTOM_IMAGE_AU') ?? defaultLegacyCustomImageBase('au');
    default:
      return readViteEnv('VITE_LEGACY_CUSTOM_IMAGE_US') ?? defaultLegacyCustomImageBase('us');
  }
}

export function buildLegacyCustomImageUrl(
  hashOrUrl: string,
  options?: LegacyCustomImageUrlOptions,
): string {
  const raw = hashOrUrl.trim();
  if (!raw) return '';
  if (isAbsoluteMediaUrl(raw)) return raw;

  const baseUrl = resolveLegacyCustomImageBaseUrl(options);
  if (!baseUrl) return '';

  const encoded = encodeURIComponent(raw);
  if (baseUrl.includes('{id}')) {
    return baseUrl.replace(/\{id\}/g, encoded);
  }
  return `${baseUrl.replace(/\/+$/, '')}/${encoded}`;
}
